import { Controller } from '@hotwired/stimulus'
import {
  shouldSkipCheckForChanges,
  type CustomChangeBroadcastPayload,
  fieldIsMultiple,
  urlFromDependencyData,
  listenToChangesOnForm,
  dependencyWithModelParent,
  broadcastInputChange
} from '../../_broadcast_change_utils'

type Option = string | [string, string | number] | { label: string; value: string | number }
type Payload = Option[] | { options: Option[]; selected: Option }

export default class extends Controller<HTMLDivElement> {
  static targets = ['spinner', 'template', 'input', 'message', 'optionsPlaceholder']

  static values = {
    path: String,
    dependency: Array,
    modelParent: String,
    blankOption: String,
    defaultValue: String,
    additionalParameters: Object,
    editMode: Boolean
  }

  declare readonly spinnerTarget: HTMLDivElement
  declare readonly templateTarget: HTMLTemplateElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly messageTarget: HTMLDivElement
  declare readonly optionsPlaceholderTarget: HTMLDivElement

  declare readonly pathValue: string
  declare readonly dependencyValue: string[]
  declare readonly modelParentValue: string
  declare readonly hasModelParentValue: boolean
  declare readonly blankOptionValue: string
  declare readonly hasBlankOptionValue: boolean
  declare readonly defaultValueValue: string
  declare readonly additionalParametersValue: Record<string, string>
  declare readonly editModeValue: boolean

  async connect(): Promise<void> {
    listenToChangesOnForm(this.inputTarget.form, e => {
      void this.#checkInputValueChangesWithinBroadcast.bind(this)(e)
    })
  }

  // deeper helpers

  #optionLabelWithBlankOptionAsFallback(label: string | number): string {
    const labelAsString = String(label)
    if (this.hasBlankOptionValue) {
      return labelAsString.length > 0 ? labelAsString : this.blankOptionValue
    }
    return labelAsString
  }

  #renderResultAsOption(result: Option): void {
    const optionFragment = this.templateTarget.content.cloneNode(true) as DocumentFragment
    const optionElement = optionFragment.firstElementChild as HTMLLabelElement
    const { label, value } = this.#labelAndValueFromResult(result)
    optionElement.innerHTML = optionElement.innerHTML
      .replace(/--label--/g, label)
      .replace(/--value--/g, value)
    this.optionsPlaceholderTarget.appendChild(optionElement)
  }

  #labelAndValueFromResult(result: Option): { label: string; value: string } {
    if (typeof result === 'string' || typeof result === 'number') {
      return {
        label: this.#optionLabelWithBlankOptionAsFallback(result),
        value: String(result)
      }
    } else if (Array.isArray(result)) {
      return {
        label: this.#optionLabelWithBlankOptionAsFallback(result[0]),
        value: String(result[1])
      }
    }
    return {
      label: this.#optionLabelWithBlankOptionAsFallback(result.label),
      value: String(result.value)
    }
  }

  // main helper

  async #checkInputValueChangesWithinBroadcast(
    eventPayload: CustomChangeBroadcastPayload | null = null
  ): Promise<void> {
    const isMultiple = fieldIsMultiple(eventPayload)

    let dependencies = this.dependencyValue
    if (this.hasModelParentValue) {
      dependencies = dependencyWithModelParent(dependencies, this.modelParentValue)
    }
    if (shouldSkipCheckForChanges(eventPayload, isMultiple, dependencies)) return

    // 1: Fetch the data
    this.messageTarget.classList.add('hidden')
    this.spinnerTarget.classList.remove('hidden')
    const url = urlFromDependencyData(
      this.pathValue,
      this.inputTarget.form,
      this.dependencyValue,
      this.modelParentValue,
      this.additionalParametersValue
    )
    if (url === null) return
    const rawResponse = await window.fetch(url)
    const feedback: Payload = await rawResponse.json()

    // 2: Render the results
    // A reload draws new options under a choice already made, and that choice outranks the
    // declared default, which only ever says where to start. Radios alone: a multiple select
    // holds several values, and carrying one of them over would quietly drop the rest.
    const chosenValue = this.optionsPlaceholderTarget.querySelector<HTMLInputElement>(
      'input[type="radio"]:checked'
    )?.value
    this.optionsPlaceholderTarget.innerHTML = ''
    this.spinnerTarget.classList.add('hidden')
    const results = 'options' in feedback ? feedback.options : feedback
    results.forEach(result => {
      this.#renderResultAsOption(result)
    })
    if (results.length === 0) {
      this.messageTarget.classList.remove('hidden')
    }
    // 2a: emit event with values to be populated in the input
    const form = this.inputTarget.form
    const labels = results.map(result => this.#labelAndValueFromResult(result).label)
    const optionFragment = this.templateTarget.content.cloneNode(true) as DocumentFragment
    const optionElement = optionFragment.firstElementChild as HTMLLabelElement
    const fieldName = optionElement.querySelector('input')?.name
    form?.dispatchEvent(new CustomEvent('ui-input:populate', { detail: { fieldName, labels } }))

    // 3: Set the selected value (for predefined selected value or for the first option)
    if (results.length === 0) return
    const wantedValue =
      chosenValue ?? (this.defaultValueValue === '' ? null : this.defaultValueValue)
    let selectedOption: Option | null = wantedValue

    if (this.defaultValueValue === ':first') {
      selectedOption = results[0]
    } else if (this.defaultValueValue === ':first_if_one') {
      if (results.length === 1) {
        selectedOption = results[0]
        this.#broadcastInputChange()
      } else {
        selectedOption = null
      }
    } else if (Array.isArray(results[0]) && wantedValue !== null) {
      selectedOption =
        (results as Array<[string, string]>).find(option => String(option[1]) === wantedValue) ??
        null
      if (this.editModeValue && !selectedOption && results.length === 1) {
        selectedOption = results[0]
      }
    } else if (selectedOption === '[]') {
      selectedOption = null
    }

    if (this.editModeValue && selectedOption !== null) {
      this.#broadcastInputChange()
    }

    const referenceOptionFallback = 'selected' in feedback ? feedback.selected : ''
    const labelValue = this.#labelAndValueFromResult(selectedOption ?? referenceOptionFallback)
    const { label: labelToShow, value: valueToSelect } = labelValue

    // There are two places in the select input in which we need to set the value.

    // 3a: Set the option as selected
    this.optionsPlaceholderTarget.querySelectorAll('input').forEach(input => {
      const inputValue = input.getAttribute('value')
      if (inputValue === valueToSelect) {
        input.setAttribute('checked', 'checked')
      } else {
        input.removeAttribute('checked')
      }
    })

    // 3b: Set the label to show in the trigger button
    const label = this.element.closest('ui-input-select')?.querySelector('button > div:first-child')
    if (label !== null && label !== undefined) {
      label.innerHTML = this.#optionLabelWithBlankOptionAsFallback(labelToShow) ?? ''
    }
  }

  #broadcastInputChange() {
    setTimeout(() => {
      const optionInput = this.optionsPlaceholderTarget.querySelector('input')
      if (optionInput !== null) broadcastInputChange(optionInput)
    }, 500)
  }
}
